import React, { memo, useMemo, useState } from 'react';
import type { TextFieldProps } from '@mui/material';
import { createFilterOptions, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import type { LineItemConfigurationResultDto } from '@pflegenavi/service-provider-shared/api-endpoints';
import type { LineItem } from '../ServiceProviderReceiptBatchForm';

interface ServiceProviderQuickResidentsServiceSelectionProps {
  lineItem: LineItem;
  setLineItem: React.Dispatch<React.SetStateAction<LineItem>>;
  setName: (name: string) => void;
  lineItemConfigurations: LineItemConfigurationResultDto[] | undefined;
  textFieldProps?: TextFieldProps;
}

export const ServiceProviderQuickResidentsServiceSelection: React.FC<ServiceProviderQuickResidentsServiceSelectionProps> =
  memo(
    ({
      lineItem,
      setLineItem,
      setName,
      lineItemConfigurations,
      textFieldProps,
    }) => {
      const [open, setOpen] = useState(false);

      const autoCompleteOptions = useMemo(
        () =>
          lineItemConfigurations?.map((item) => ({
            value: item as LineItemConfigurationResultDto | undefined,
            label: item.name,
          })),
        [lineItemConfigurations]
      );

      const chosenAutoCompleteOption = useMemo(
        () =>
          autoCompleteOptions?.find(
            (item) => item.value?.name === lineItem.name
          ) ?? {
            label: lineItem.name ?? '',
            value: undefined,
          },
        [lineItem.name, autoCompleteOptions]
      );

      return (
        <Autocomplete
          value={chosenAutoCompleteOption}
          open={open}
          freeSolo={true}
          onFocus={() => {
            setOpen(true);
          }}
          onBlur={() => {
            setOpen(false);
          }}
          onChange={(_, option) => {
            setOpen(false);
            if (
              option !== undefined &&
              typeof option === 'object' &&
              option.value !== undefined
            ) {
              setLineItem((prev) => ({
                ...prev,
                name: option.value?.name,
                amount: option.value?.amount ? option.value.amount : undefined,
                taxes:
                  option.value?.taxCodes && option.value.taxCodes.length > 0
                    ? [
                        {
                          ...option.value?.taxCodes[0],
                          taxAmountInPercentage:
                            option.value?.taxCodes[0]?.taxAmount * 100,
                        },
                      ]
                    : undefined,
              }));
            }
          }}
          filterOptions={createFilterOptions({ limit: 7 })}
          inputValue={lineItem.name}
          onInputChange={(_, value) => {
            setName(value);
          }}
          isOptionEqualToValue={(option, value) =>
            option.value !== undefined &&
            value.value !== undefined &&
            option.value.name === value.value.name
          }
          disableClearable={true}
          renderInput={({ ...params }) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              {...textFieldProps}
              sx={{
                ...(textFieldProps ? textFieldProps.sx : {}),
                '& .MuiInputBase-root.MuiOutlinedInput-root': {
                  borderColor: 'primary.main',
                  borderWidth: 1,
                },
              }}
            />
          )}
          options={autoCompleteOptions ?? []}
        />
      );
    }
  );
